import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '@/lib/api';
import { formatRp, formatDate, formatNumber } from '@/lib/format';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, ArrowLeft, PackageSearch } from 'lucide-react';
import { toast } from 'sonner';

export default function MaterialConsumptionPage() {
  const { pid } = useParams();
  const [items, setItems] = useState([]);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [p, c] = await Promise.all([
          api.get(`/projects/${pid}`),
          api.get(`/projects/${pid}/material-consumption`),
        ]);
        setProject(p.data);
        setItems(c.data);
      } catch (e) { toast.error('Gagal memuat'); }
      finally { setLoading(false); }
    })();
  }, [pid]);

  const filtered = items.filter((m) => !search || (m.material || '').toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="animate-in-up">
      <div>
        <Link to={`/projects/${pid}`} className="inline-flex items-center gap-1.5 text-xs text-muted-fpccs hover:text-[var(--fpccs-text)]">
          <ArrowLeft className="w-3.5 h-3.5" /> Kembali ke Workspace
        </Link>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 my-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2"><PackageSearch className="w-6 h-6 text-[var(--fpccs-primary)]" /> Konsumsi Material Aktual</h1>
          <p className="text-sm text-muted-fpccs mt-1">Laporan otomatis dari transaksi pengeluaran kategori <span className="text-[var(--fpccs-primary)]">Material</span> pada project <span className="text-[var(--fpccs-text)]">{project?.name || '-'}</span>.</p>
        </div>
        <div className="relative sm:w-72">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fpccs-text-subtle)]" />
          <Input data-testid="consumption-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari material..." className="pl-9 bg-[var(--fpccs-surface-2)] border-[var(--fpccs-border)]" />
        </div>
      </div>

      <div className="card-fpccs overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-[var(--fpccs-border)] hover:bg-transparent">
              <TableHead className="text-[11px] uppercase tracking-wider text-subtle-fpccs">Material</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider text-subtle-fpccs text-right">Total Qty</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider text-subtle-fpccs">Satuan</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider text-subtle-fpccs text-right">Total Pengeluaran</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider text-subtle-fpccs text-right">Harga Rata-rata</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider text-subtle-fpccs text-right">Jml Pembelian</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider text-subtle-fpccs">Supplier Favorit</TableHead>
              <TableHead className="text-[11px] uppercase tracking-wider text-subtle-fpccs">Pembelian Terakhir</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? [...Array(6)].map((_, i) => <TableRow key={i}>{[...Array(8)].map((_, j) => <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>)}</TableRow>) :
             filtered.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center text-muted-fpccs py-10">Belum ada data. Input pengeluaran material terlebih dahulu.</TableCell></TableRow> :
             filtered.map((m, idx) => (
              <TableRow key={idx} data-testid={`consumption-row-${m.material}`} className="border-b border-[var(--fpccs-border)] hover:bg-white/[0.03]">
                <TableCell className="font-medium">{m.material}</TableCell>
                <TableCell className="text-right font-mono">{formatNumber(m.total_qty)}</TableCell>
                <TableCell className="text-xs text-muted-fpccs">{m.unit || '-'}</TableCell>
                <TableCell className="text-right font-mono font-semibold">{formatRp(m.total_amount)}</TableCell>
                <TableCell className="text-right font-mono">{formatRp(m.avg_price)}</TableCell>
                <TableCell className="text-right font-mono">{m.purchases}</TableCell>
                <TableCell className="text-xs">{m.top_supplier || '-'}</TableCell>
                <TableCell className="text-xs text-muted-fpccs">{formatDate(m.last_purchase)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
